import { Table } from 'antd'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { PerAvatarPlus } from '../../../components/custom_components/AvatarPlus'
import { SortOrder } from 'antd/lib/table'
import { LoadingAndErrors } from '../../../components/custom_components/LoadingAndErrors'
import { MeetingTag } from '../../../components/custom_components/MeetingTag'
import { PerModal } from '../../../components/custom_components/Modal'
import { PerTableActionButtons } from '../../../components/custom_components/TableActionButtons'
import PerText from '../../../components/custom_components/Text'
import { useAuth } from '../../../context/auth-context'
import useReplyerMeetingsHistory from '../../../graphql/meeting/hooks/useReplyerMeetingsHistory'
import { useDateTime } from '../../../shared/useDateTime'
import { Employee, EmployeeDisplayTinyInfo, MeetingType } from '../../../types'
import { getConclusionHrefByMeeting } from '../../meeting/util/hrefUtils'
import { NameDisplayPreference } from '../../../graphql/enum/name-display-preference.enum'

type Props = {
  isOpen: boolean
  employee: EmployeeDisplayTinyInfo
  onCancel: () => void
  isResigned: boolean
}

export const MeetingHistoryModal: FC<Props> = ({ isOpen, onCancel, employee, isResigned }) => {
  const [t] = useTranslation()
  const { user } = useAuth()
  const { formatDate, compareDates } = useDateTime(user)
  const { data, loading, error } = useReplyerMeetingsHistory(employee.id, isResigned)
  if (loading) {
    return <div></div>
  }

  if (!data || !data.meetingsHistoryByReplyer || error) {
    return <LoadingAndErrors hasNoData={!data} isLoading={loading} error={error} />
  }
  const meetings = data.meetingsHistoryByReplyer.map((meeting) =>
    meeting.reviewer ? meeting : { ...meeting, confirmationDate: undefined },
  )

  const getConclusionHrefByMeetingId = (id: number) => {
    const meeting = meetings.find((m) => m.id === id)
    return getConclusionHrefByMeeting(meeting)
  }
  const columns = [
    {
      key: 'meetingHistory-date',
      title: t('appraisalMeeting.stepOverview.history.date'),
      dataIndex: 'displayDate',
      width: '20%',
      sorter: (meetingA: { displayDate: string }, meetingB: { displayDate: string }) => {
        return compareDates(meetingA.displayDate, meetingB.displayDate)
      },
      defaultSortOrder: 'descend' as SortOrder,
      render: (dataIndex: string) => <PerText>{formatDate(dataIndex)}</PerText>,
    },
    {
      key: 'meetingHistory-type',
      title: t('appraisalMeeting.stepOverview.history.type'),
      dataIndex: 'type',
      width: '20%',
      render: (dataIndex: MeetingType) => <MeetingTag type={dataIndex} />,
    },
    {
      key: 'meetingHistory-confirmationDate',
      title: t('appraisalMeeting.stepOverview.history.confirmationDate'),
      dataIndex: 'confirmationDate',
      className: 'textAlign-center',
      width: '20%',
      render: (dataIndex: string) => (
        <PerText color="secondary" fontSize="xs">
          {dataIndex ? formatDate(dataIndex) : '-'}
        </PerText>
      ),
    },
    {
      key: 'meetingHistory-reviewer',
      title: t('appraisalMeeting.stepOverview.history.reviewer'),
      dataIndex: 'reviewer',
      render: (reviewer: Employee) =>
        !reviewer ? (
          '-'
        ) : (
          <PerAvatarPlus
            nameDisplayPreference={user.nameDisplayPreference as NameDisplayPreference}
            employeeId={reviewer.id}
            firstName={reviewer.legalFirstName}
            lastName={reviewer.legalLastName}
          />
        ),
    },
    {
      key: 'meetingHistory-actions',
      title: t('appraisalMeeting.stepOverview.history.conclusion'),
      className: 'textAlign-center',
      width: '150px',
      dataIndex: 'id',
      render: (dataIndex: number) => (
        <PerTableActionButtons
          variant="bold"
          buttons={[
            {
              icon: 'view_fill',
              color: 'primary',
              tooltip: t('tooltip.view'),
              asLink: getConclusionHrefByMeetingId(dataIndex),
            },
          ]}
        />
      ),
    },
  ]

  return (
    <PerModal
      title={{
        heading: t('employeeList.resignedEmployeeList.meetingsModal.heading'),
        subheading: t('employeeList.resignedEmployeeList.meetingsModal.subHeading', {
          employeeName: `${employee.firstName} ${employee.lastName}`,
        }),
      }}
      visible={isOpen}
      onCancel={onCancel}
      size="large"
    >
      <Table
        dataSource={meetings.map((meetingHistory) => ({ ...meetingHistory, key: meetingHistory.id }))}
        columns={columns}
        pagination={false}
      />
    </PerModal>
  )
}
